import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT, useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/smooth";

const LINKS = ["services", "routes", "fleet", "quote"];

export const LangSwitch = ({ testid = "language-switch-button" }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid={testid} className="inline-flex w-fit rounded-full border border-white/15 p-0.5 font-mono-tc text-xs">
      {["pl", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${testid}-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1.5 uppercase transition-colors duration-300 ${lang === l ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Nav() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${scrolled || open ? "border-b border-white/10 bg-[#090A0C]/80 backdrop-blur-xl" : ""}`}>
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("top")} data-testid="nav-home-link"><Logo /></button>
        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((k) => (
            <button key={k} data-testid={`nav-link-${k}`} onClick={() => go(k)} className="group relative font-mono-tc text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:text-white">
              {t.nav[k]}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-white transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block"><LangSwitch /></div>
          <a href={CONTACT.tel} data-testid="nav-call-button" className="hidden items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-zinc-300 md:flex">
            <Phone className="h-4 w-4" /> {CONTACT.phone}
          </a>
          <button data-testid="nav-mobile-menu-button" onClick={() => setOpen((o) => !o)} className="rounded-full border border-white/15 p-2.5 lg:hidden" aria-label="menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden lg:hidden" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-1 px-5 pb-6">
              {LINKS.map((k) => (
                <button key={k} data-testid={`mobile-nav-link-${k}`} onClick={() => go(k)} className="border-b border-white/10 py-4 text-left font-display text-3xl font-bold uppercase italic">
                  {t.nav[k]}
                </button>
              ))}
              <div className="mt-4 flex items-center justify-between">
                <LangSwitch testid="mobile-language-switch" />
                <a href={CONTACT.tel} data-testid="mobile-nav-call-button" className="flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-black">
                  <Phone className="h-4 w-4" /> {t.nav.call}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
